import React from "react"
import tw, { styled, css } from "twin.macro"
import { useState } from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import { graphql } from "gatsby"
import PreHeader from "../../atoms/pre-header"
import Sandbox from "../../atoms/sandbox"
import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, {
  Thumbs,
  Mousewheel,
  Virtual,
  Navigation,
  A11y,
} from "swiper/core"
import { useMediaQuery } from "../../../utils/use-media-query"
import ZoomImage from "../../molecules/zoom-image"
import AppIcon from "@atoms/icon"
import "swiper/swiper.scss"
import "swiper/components/thumbs/thumbs.scss"
import "./photo-gallery.css"

SwiperCore.use([Thumbs, Mousewheel, Virtual, Navigation, A11y])

const PhotoGalleryThumb = ({ thumb, selected }) => (
  <div
    tw="transition-opacity overflow-hidden"
    css={[!selected && tw`opacity-60`]}
  >
    <GatsbyImage image={thumb} alt="" />
  </div>
)

const PGTitle = styled(PreHeader)`
  color: rgba(255, 255, 255, 0.87);
  font-weight: 400;
  margin-bottom: 0.5rem;
`

const PGNavButton = styled.button(({ isPrev }) => [
  tw`absolute block z-50 text-white focus:(outline-none text-brand-green)`,
  css`
    top: 120px;
    @media (min-width: 640px) {
      top: 50%;
      margin-top: -80px;
    }
  `,
  isPrev ? tw`left-0 xl:-ml-2` : tw`right-0 xl:-mr-2`,
])

const PhotoGallery = ({ items, className, skipScrollLockClear }) => {
  const pics = items.map(pic => ({
    id: pic.id,
    title: pic.title,
    text: pic.description,
    thumb: getImage(pic.thumb.localFile),
    main: getImage(pic.main.localFile),
    expanded: getImage(pic.expanded.localFile),
  }))
  const [activeIndex, setActiveIndex] = useState(0)
  const [thumbsSwiper, setThumbsSwiper] = useState(null)
  const [expanded, setExpanded] = useState(null)
  const isVertical = useMediaQuery("(min-width: 640px)")
  const isActivated = id => {
    return pics[activeIndex - 1]?.id === id
  }

  return (
    <>
      <div
        tw="relative min-h-full w-full mx-auto"
        className={className}
        css={css`
          max-height: 540px;
          max-width: 1180px;
          @media (min-aspect-ratio: 1/1) {
            max-height: none;
            height: 100%;
          }
        `}
      >
        <div
          tw="p-6 sm:(w-3/4 absolute left-0 top-0 h-full flex items-center)"
          css={css`
            max-width: 788px;
          `}
        >
          <Swiper
            loop={true}
            thumbs={{ swiper: thumbsSwiper }}
            navigation={{
              nextEl: ".pg-swiper-next-button",
              prevEl: ".pg-swiper-prev-button",
            }}
            onSlideChange={({ activeIndex }) => setActiveIndex(activeIndex)}
          >
            {pics.map((pic, index) => (
              <SwiperSlide key={pic.id} virtualIndex={index}>
                <GatsbyImage image={pic.main} alt="selected.title" tw="mb-4" />
                <div tw="flex">
                  <PGTitle>{pic.title}</PGTitle>
                  <span tw="flex-grow"></span>
                  {pic.expanded && (
                    <button onClick={() => setExpanded(pic.expanded)}>
                      <AppIcon
                        name="open_in_full"
                        tw="w-4 h-4 block text-white"
                      />
                    </button>
                  )}
                </div>
                <Sandbox
                  tw="text-sm text-white text-opacity-60"
                  dangerouslySetInnerHTML={{ __html: pic.text }}
                ></Sandbox>
              </SwiperSlide>
            ))}
          </Swiper>
          <PGNavButton
            isPrev={true}
            className="pg-swiper-prev-button"
            tw="absolute"
          >
            <AppIcon name="arrow_back" tw="w-6 h-6 block" />
          </PGNavButton>
          <PGNavButton className="pg-swiper-next-button" tw="absolute">
            <AppIcon name="arrow_forward" tw="w-6 h-6 block" />
          </PGNavButton>
        </div>
        <Swiper
          tw="w-full sm:(w-1/4 h-full absolute top-0 right-0 overflow-hidden pr-6)"
          className="app-photo-gallery-thumbs"
          freeMode={true}
          slidesPerView={isVertical ? "auto" : 3}
          direction={isVertical ? "vertical" : "horizontal"}
          spaceBetween={20}
          onSwiper={setThumbsSwiper}
          centeredSlides={true}
          mousewheel={true}
          autoHeight={true}
          watchOverflow={true}
          watchSlidesVisibility={true}
          watchSlidesProgress={true}
        >
          {pics.map(pic => (
            <SwiperSlide key={pic.id}>
              {({ isActive }) => (
                <PhotoGalleryThumb
                  thumb={pic.thumb}
                  selected={isActive || isActivated(pic.id)}
                />
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      {expanded && (
        <ZoomImage
          image={expanded}
          skipScrollLockClear={skipScrollLockClear}
          onClose={() => setExpanded(null)}
        />
      )}
    </>
  )
}

export const fragment = graphql`
  fragment GalleryPicture on FT_ComponentMoleculesPicture {
    id
    title
    description
    thumb: image {
      id
      url
      localFile {
        childImageSharp {
          gatsbyImageData(
            aspectRatio: 1.5
            width: 290
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
    main: image {
      id
      url
      localFile {
        childImageSharp {
          gatsbyImageData(
            aspectRatio: 1.5
            width: 740
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
    expanded: image {
      id
      url
      localFile {
        childImageSharp {
          gatsbyImageData(
            aspectRatio: 1.5
            width: 1280
            formats: [AUTO, WEBP, AVIF]
          )
        }
      }
    }
  }
`

export default PhotoGallery
